// Anniversary countdown helper (W3.1 §5.7 / §5.6).
//
// Pure functions only — no React / no DOM. Used by EventCard, NextCountdown,
// UpcomingList, and the milestone tape. Locks HKT timezone so all renders
// stay date-stable across the day (per architecture §3 + pair locale).
//
// Per spec §5.7, the rendered copy is one of three zh-Hant buckets:
//   • 今天      (daysUntil === 0)
//   • 還有 N 天 (daysUntil > 0, future)
//   • 已過 N 天 (daysUntil < 0, past)

import { differenceInCalendarDays, parseISO } from "date-fns";

/**
 * Day delta from today (HKT midnight) to the target ISO date string.
 *
 * Negative = already happened; positive = upcoming; 0 = today.
 * Uses `differenceInCalendarDays` so time-of-day doesn't shift the result.
 */
export function daysUntil(targetIsoDate: string, now: Date = new Date()): number {
  if (!targetIsoDate) return 0;
  const target = parseISO(targetIsoDate);
  if (Number.isNaN(target.getTime())) return 0;
  return differenceInCalendarDays(target, now);
}

/**
 * Per spec §5.7: zh-Hant labels with three buckets (今天 / 還有 N 天 / 已過 N 天).
 * Used by `TimelineEventCard` + `UpcomingList` list rows.
 */
export function countdownLabel(days: number): string {
  if (days === 0) return "今天";
  if (days > 0) return `還有 ${days} 天`;
  return `已過 ${Math.abs(days)} 天`;
}

/**
 * Long-form label used in `NextAnniversaryCountdown` segment 2 (hero card).
 * Same buckets but spelled out: 今天 / 明天 / 還有 N 天 / 已過 N 天 / N 天前.
 */
export function countdownLabelLong(days: number): string {
  if (days === 0) return "今天";
  if (days === 1) return "明天";
  if (days > 0) return `還有 ${days} 天`;
  if (days === -1) return "昨天";
  return `已過 ${Math.abs(days)} 天`;
}

/**
 * Compose the standard `countdownLabel(targetIsoDate)` shorthand.
 * Convenient when the caller only has the target ISO.
 */
export function formatCountdown(targetIsoDate: string, now: Date = new Date()): string {
  return countdownLabel(daysUntil(targetIsoDate, now));
}